/**
 * Knowledge Map Utilities
 *
 * Merge GeoJSON from completed quizzes and style by completion rate.
 */

import type { FeatureCollection, Feature, GeoJsonProperties, Geometry } from "geojson";
import type { KnowledgeState, QuizConfig } from "@/db/types";
import { fetchGeoJSONFromUrl, getFeatureIdentifier } from "./geojsonLoader";
import { db } from "@/db/database";
import { evaluateFilters } from "./filterUtils";

/**
 * Merge GeoJSON from all quizzes for a set of knowledge states
 */
export async function buildKnowledgeMap(
  knowledgeStates: KnowledgeState[] | Array<{
    region: string;
    granularityLevel: number;
    completionRate: number;
    category: any;
    isPerfect: boolean;
  }>
): Promise<FeatureCollection<Geometry, GeoJsonProperties>> {
  if (knowledgeStates.length === 0) {
    return {
      type: "FeatureCollection",
      features: [],
    };
  }

  // For combined view (simplified objects), we need to get the actual KnowledgeState objects
  // to access datasetSignature
  const actualStates: KnowledgeState[] = [];

  for (const state of knowledgeStates) {
    if ('datasetSignature' in state) {
      actualStates.push(state as KnowledgeState);
    } else {
      // Fetch the actual knowledge state from DB using compound index
      const fullState = await db.knowledge_state
        .where("[category+region+granularityLevel]")
        .equals([state.category, state.region, state.granularityLevel])
        .first();
      if (fullState) {
        actualStates.push(fullState);
      }
    }
  }

  // Load quiz configs for each knowledge state to get filters and dataset info
  const statesWithConfigs = await Promise.all(
    actualStates.map(async (state) => {
      const config = await db.quiz_configs
        .where("signature")
        .equals(state.lastQuizSignature)
        .first();
      return { state, config };
    })
  );

  // Filter out states without configs or without datasetSignature (type guard)
  const validStates = statesWithConfigs.filter((sc): sc is { state: KnowledgeState; config: QuizConfig } =>
    sc.config !== undefined && sc.state.datasetSignature !== undefined
  );

  console.log(`📊 Processing ${validStates.length} knowledge states for knowledge map`);

  // Log what we're about to draw
  console.group("🗺️ Knowledge states to draw:");
  const byGeography = new Map<string, any[]>();
  validStates.forEach(({ state }) => {
    if (!byGeography.has(state.geography)) {
      byGeography.set(state.geography, []);
    }
    byGeography.get(state.geography)!.push({
      region: state.region,
      prompts: state.totalPrompts,
      completion: Math.round(state.completionRate) + '%',
      datasetSig: state.datasetSignature ? state.datasetSignature.substring(0, 40) + '...' : 'N/A'
    });
  });

  byGeography.forEach((states, geography) => {
    console.log(`${geography} (${states.length} quiz${states.length > 1 ? 'zes' : ''}):`);
    states.forEach(s => console.log(`  - ${s.region}: ${s.prompts} prompts @ ${s.completion}`));
  });
  console.groupEnd();

  // Group by datasetSignature to avoid loading same dataset multiple times
  const datasetSignatures = new Set(
    validStates.map((sc) => sc.state.datasetSignature)
  );

  // Load all unique datasets
  const datasetPromises = Array.from(datasetSignatures).map(async (signature) => {
    // Find a config with this dataset signature
    const stateConfig = validStates.find((sc) => sc.state.datasetSignature === signature);
    const config = stateConfig?.config;

    if (!config?.datasetSource) {
      console.warn(`⚠️ No quiz config found for dataset signature: ${signature}`);
      return null;
    }

    // Load the GeoJSON
    if (config.datasetSource.type === "dataset-url" && config.datasetSource.path) {
      try {
        const collection = await fetchGeoJSONFromUrl(config.datasetSource.path);
        return { signature, collection };
      } catch (error) {
        console.error(`❌ Failed to load dataset ${signature}:`, error);
        return null;
      }
    }

    // If stored inline, parse it
    if (config.storedCollection) {
      try {
        const collection = JSON.parse(config.storedCollection);
        return { signature, collection };
      } catch (error) {
        console.error(`❌ Failed to parse stored collection ${signature}:`, error);
        return null;
      }
    }

    return null;
  });

  const datasets = (await Promise.all(datasetPromises)).filter(
    (d): d is { signature: string; collection: FeatureCollection } => d !== null
  );

  console.log(`✅ Loaded ${datasets.length} unique datasets`);

  // Use a Map to track best knowledge for each feature (handles overlapping quizzes)
  const featureKnowledgeMap = new Map<string, {
    feature: Feature<Geometry, GeoJsonProperties>;
    knowledge: any;
  }>();

  // For each dataset, process all knowledge states that use it
  datasets.forEach(({ signature: datasetSignature, collection }) => {
    // Find all knowledge states that use this dataset
    const statesForDataset = validStates.filter(
      (sc) => sc.state.datasetSignature === datasetSignature
    );

    // Process each knowledge state separately
    statesForDataset.forEach(({ state: knowledge, config: quizConfig }) => {
      collection.features.forEach((feature, featureIndex) => {
        const featureId = getFeatureIdentifier(feature, featureIndex);

        // Check if this feature was part of THIS specific quiz
        const isExcluded =
          quizConfig.excludedFeatureIds &&
          quizConfig.excludedFeatureIds.includes(featureId);

        // Convert StoredFilterRule to FilterRule format (add id field)
        const filters = quizConfig.filters.map((f: any, index: number) => ({
          ...f,
          id: `filter-${index}`,
        }));

        const passesFilters =
          !quizConfig.filters ||
          quizConfig.filters.length === 0 ||
          evaluateFilters(feature.properties || {}, filters);

        const shouldApplyKnowledge = !isExcluded && passesFilters;

        if (shouldApplyKnowledge) {
          // Create feature key for deduplication (use geometry as unique identifier)
          const featureKey = JSON.stringify(feature.geometry);

          // Prepare knowledge metadata from THIS knowledge state
          const knowledgeMetadata = {
            region: knowledge.region,
            completionRate: knowledge.completionRate,
            granularityLevel: knowledge.granularityLevel,
            achievedPerfection: knowledge.achievedPerfection,
            perfectCompletionDate: knowledge.perfectCompletionDate,
            lastUpdated: knowledge.lastUpdated, // For color coding based on last practice
            category: knowledge.category,
            totalPrompts: knowledge.totalPrompts,
            completedPrompts: knowledge.completedPrompts,
            lastQuizSignature: knowledge.lastQuizSignature,
          };

          // Check if we already have knowledge for this feature
          const existing = featureKnowledgeMap.get(featureKey);

          if (!existing) {
            // First time seeing this feature, add it
            featureKnowledgeMap.set(featureKey, {
              feature,
              knowledge: knowledgeMetadata,
            });
          } else {
            // Feature already exists - pick the better knowledge
            // Priority: 100% completion > more questions > higher completion rate
            const shouldReplace = isBetterKnowledge(knowledgeMetadata, existing.knowledge);

            if (shouldReplace) {
              featureKnowledgeMap.set(featureKey, {
                feature,
                knowledge: knowledgeMetadata,
              });
            }
          }
        }
        // Note: We intentionally DON'T add features without knowledge.
        // Only features that match at least one quiz's filters should appear on the map.
      });
    });
  });

  // Convert map to features array
  const allFeatures: Feature<Geometry, GeoJsonProperties>[] = [];
  featureKnowledgeMap.forEach(({ feature, knowledge }) => {
    allFeatures.push({
      ...feature,
      properties: {
        ...feature.properties,
        __knowledge: knowledge,
      },
    });
  });

  console.log(`🗺️ Built knowledge map with ${allFeatures.length} features`);

  // Log summary by geography
  console.group("📍 Features added to map by geography:");
  const featuresByGeo = new Map<string, number>();
  allFeatures.forEach(f => {
    const geo = f.properties?.__knowledge?.region || 'Unknown';
    featuresByGeo.set(geo, (featuresByGeo.get(geo) || 0) + 1);
  });

  Array.from(featuresByGeo.entries())
    .sort((a, b) => b[1] - a[1])
    .forEach(([geo, count]) => {
      console.log(`${geo}: ${count} features`);
    });
  console.groupEnd();

  return {
    type: "FeatureCollection",
    features: allFeatures,
  };
}

/**
 * Determine if new knowledge is better than existing knowledge
 * Priority:
 * 1. 100% completion (achievedPerfection) beats everything
 * 2. More questions (totalPrompts) is better at same completion
 * 3. Higher completion rate
 */
function isBetterKnowledge(newKnowledge: any, existingKnowledge: any): boolean {
  // If existing has 100% but new doesn't, keep existing
  if (existingKnowledge.achievedPerfection && !newKnowledge.achievedPerfection) {
    return false;
  }

  // If new has 100% but existing doesn't, use new
  if (newKnowledge.achievedPerfection && !existingKnowledge.achievedPerfection) {
    return true;
  }

  // Both have 100% OR neither has 100%
  // Next priority: more questions
  if (newKnowledge.totalPrompts !== existingKnowledge.totalPrompts) {
    return newKnowledge.totalPrompts > existingKnowledge.totalPrompts;
  }

  // Same number of questions, use higher completion rate
  return newKnowledge.completionRate > existingKnowledge.completionRate;
}

/**
 * Deduplicate overlapping knowledge states per country
 *
 * NEW RULES (aggressive deduplication):
 * - Combined view: Show only 1 quiz per geography across ALL categories
 * - Category view: Show only 1 quiz per geography within that category
 * - Selection logic:
 *   1. If any quiz at 100%, pick the one with most totalPrompts
 *   2. If none at 100%, pick the one with most completedPrompts (e.g., 121/212 beats 19/20)
 *
 * This prevents double-counting when you have multiple quizzes covering the same geography
 * (e.g., Spain Provinces, Spain Autonomous Communities, Spain Phone Codes).
 *
 * @param states - Knowledge states to deduplicate
 * @param mode - 'combined' (all categories) or specific category
 */
export function deduplicateKnowledgeStates<
  T extends {
    geography?: string;
    region: string;
    totalPrompts: number;
    completedPrompts: number;
    achievedPerfection: boolean;
    category?: string;
    lastUpdated: string;
    perfectCompletionDate?: string | null;
  }
>(states: T[], mode: 'combined' | string = 'combined'): T[] {
  // Step 1: Filter to category if not combined view
  const filtered = mode === 'combined'
    ? states
    : states.filter(s => s.category === mode);

  // Step 2: Group by geography only (ignore category for combined view)
  const geographyGroups = new Map<string, T[]>();

  filtered.forEach((state) => {
    // Use geography if available, fallback to region (quiz name) for backwards compatibility
    const geography = state.geography || state.region;

    const existing = geographyGroups.get(geography) || [];
    existing.push(state);
    geographyGroups.set(geography, existing);
  });

  const deduplicated: T[] = [];

  // Step 3: For each geography, pick the BEST quiz
  geographyGroups.forEach((groupStates, geography) => {
    if (groupStates.length === 1) {
      // Only one quiz for this geography, keep it
      deduplicated.push(groupStates[0]!);
      return;
    }

    // Multiple quizzes for this geography - pick the best one
    const perfectStates = groupStates.filter((s) => s.achievedPerfection);

    let bestState: T;

    if (perfectStates.length > 0) {
      // Rule 1: At least one quiz at 100% - pick the one with most totalPrompts
      // Tie-breaker: If same totalPrompts, pick the most recently practiced (for fresh color coding)
      const sorted = perfectStates.sort((a, b) => {
        // First priority: most totalPrompts
        if (b.totalPrompts !== a.totalPrompts) {
          return b.totalPrompts - a.totalPrompts;
        }
        // Tie-breaker: most recent lastUpdated (most recently practiced)
        const dateA = new Date(a.lastUpdated).getTime();
        const dateB = new Date(b.lastUpdated).getTime();
        return dateB - dateA; // More recently practiced is better
      });
      bestState = sorted[0]!;

      const filteredNames = groupStates
        .filter(s => s !== bestState)
        .map(s => {
          const category = s.category ? ` [${s.category}]` : '';
          const completion = s.achievedPerfection ? '100%' : `${s.completedPrompts}/${s.totalPrompts}`;
          return `"${s.region}"${category} (${completion})`;
        })
        .join(', ');

      console.log(
        `🔍 Deduplication [${mode}] for ${geography}: ` +
        `Keeping "${bestState.region}" (${bestState.totalPrompts} @ 100%), ` +
        `filtering out ${filteredNames}`
      );
    } else {
      // Rule 2: None at 100% - pick the one with most completedPrompts (not percentage!)
      // Tie-breaker: If same completedPrompts, pick the most recent attempt
      const sorted = groupStates.sort((a, b) => {
        // First priority: most completedPrompts
        if (b.completedPrompts !== a.completedPrompts) {
          return b.completedPrompts - a.completedPrompts;
        }
        // Tie-breaker: most recent lastUpdated
        const dateA = new Date(a.lastUpdated).getTime();
        const dateB = new Date(b.lastUpdated).getTime();
        return dateB - dateA; // Newer is better
      });
      bestState = sorted[0]!;

      const filteredNames = groupStates
        .filter(s => s !== bestState)
        .map(s => {
          const category = s.category ? ` [${s.category}]` : '';
          return `"${s.region}"${category} (${s.completedPrompts}/${s.totalPrompts})`;
        })
        .join(', ');

      console.log(
        `🔍 Deduplication [${mode}] for ${geography}: ` +
        `Keeping "${bestState.region}" (${bestState.completedPrompts}/${bestState.totalPrompts} correct), ` +
        `filtering out ${filteredNames}`
      );
    }

    deduplicated.push(bestState);
  });

  return deduplicated;
}

/**
 * Get color based on recency of last practice (for review timing)
 * Green (< 1 week) -> Yellow (< 2 weeks) -> Orange (< 1 month) -> Red (older)
 * Gray if not perfect yet
 */
export function getCompletionColor(
  achievedPerfection: boolean,
  lastUpdated: string | null,
  completionRate: number
): string {
  // Not perfect yet - show progress as blue shades
  if (!achievedPerfection || !lastUpdated) {
    if (completionRate === 0) {
      return "#9ca3af"; // Gray - no knowledge
    }

    // Blue shades for in-progress (lighter = less complete)
    const normalized = Math.min(100, Math.max(0, completionRate)) / 100;
    const saturation = 40 + normalized * 50; // 40% -> 90%
    const lightness = 70 - normalized * 35; // 70% -> 35%
    return `hsl(210, ${saturation}%, ${lightness}%)`;
  }

  // Perfect score - color by freshness of LAST practice (for review timing)
  // Use green gradient: bright green (fresh) -> dark faded green (stale)
  const now = new Date();
  const lastPracticeDate = new Date(lastUpdated);
  const daysSince = (now.getTime() - lastPracticeDate.getTime()) / (1000 * 60 * 60 * 24);

  if (daysSince < 7) {
    // Bright green - fresh in mind (< 1 week)
    return "hsl(142, 70%, 45%)"; // Vibrant green
  } else if (daysSince < 14) {
    // Medium green - getting stale (1-2 weeks)
    return "hsl(142, 60%, 35%)"; // Slightly darker
  } else if (daysSince < 30) {
    // Dark green - needs review (2-4 weeks)
    return "hsl(142, 50%, 28%)"; // Darker still
  } else {
    // Very dark, faded green - old, faded from mind (> 1 month)
    return "hsl(142, 40%, 22%)"; // Very dark green
  }
}

/**
 * Get fill opacity based on completion rate
 * More transparent to allow map labels to show through
 */
export function getCompletionOpacity(completionRate: number): number {
  if (completionRate === 0) {
    return 0.15; // Very faint for no knowledge
  }

  // 0.3 -> 0.5 based on completion (more transparent than before)
  return 0.3 + (completionRate / 100) * 0.2;
}
